import React, { useState, useCallback } from 'react';
import { doc, getDoc, updateDoc, writeBatch, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import DictionarySearch from '../components/dictionary/DictionarySearch';
import DictionaryEntryForm from '../components/dictionary/DictionaryEntryForm';
import QuickAddChips from '../components/dictionary/QuickAddChips';
import DisplayItemsEditor from '../components/dictionary/DisplayItemsEditor';
import DisplayItemsList from '../components/dictionary/DisplayItemsList';
import './PageShell.css';
import './DictionaryPage.css';

const getMeaning = (entry) => {
  if (entry.meaningEnglish) return entry.meaningEnglish;
  const firstItem = (entry.displayItems || [])[0];
  if (firstItem && firstItem.type === 'text' && firstItem.value) return firstItem.value;
  return '';
};

const formatCount = (n) => (n && n > 0) ? `used ${n} ${n === 1 ? 'time' : 'times'}` : null;

function DictionaryPage() {
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormTerm, setAddFormTerm] = useState('');

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDisplayItemForm, setShowDisplayItemForm] = useState(false);

  const handleSelect = (entry) => {
    setSelectedEntry(entry);
    setShowAddForm(false);
    resetEditing();
  };

  const handleNotFound = (term) => {
    setSelectedEntry(null);
    setAddFormTerm(term);
    setShowAddForm(true);
    resetEditing();
  };

  const handleSaved = (entry) => {
    setSelectedEntry(entry);
    setShowAddForm(false);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
  };

  const resetEditing = () => {
    setShowDeleteConfirm(false);
    setShowDisplayItemForm(false);
  };

  // --- DisplayItems handlers ---
  const handleDisplayItemSaved = (item) => {
    // Update local state with new item
    setSelectedEntry((prev) => ({
      ...prev,
      displayItems: [...(prev.displayItems || []), item],
    }));
  };

  const handleDisplayItemsUpdated = (newItems) => {
    setSelectedEntry((prev) => ({ ...prev, displayItems: newItems }));
  };

  // --- Delete entry ---
  const handleDelete = async () => {
    if (!selectedEntry) return;
    setDeleting(true);

    try {
      const entryId = selectedEntry.id;
      const batch = writeBatch(db);

      // Remove this entry's ID from all linked entries
      const allLinkedIds = new Set([
        ...(selectedEntry.synonymIds || []),
        ...(selectedEntry.antonymIds || []),
        ...(selectedEntry.registerVariantIds || []),
        ...(selectedEntry.expressionIds || []),
      ]);

      for (const linkedId of allLinkedIds) {
        const linkedRef = doc(db, 'dictionary', linkedId);
        batch.update(linkedRef, {
          synonymIds: arrayRemove(entryId),
          antonymIds: arrayRemove(entryId),
          registerVariantIds: arrayRemove(entryId),
          expressionIds: arrayRemove(entryId),
        });
      }

      // Delete the entry itself
      batch.delete(doc(db, 'dictionary', entryId));

      await batch.commit();

      setSelectedEntry(null);
      setShowDeleteConfirm(false);
    } catch (e) {
      console.error('Error deleting entry:', e);
    }
    setDeleting(false);
  };

  // Refresh entry from Firestore
  const refreshEntry = useCallback(async () => {
    if (!selectedEntry) return;
    try {
      const snap = await getDoc(doc(db, 'dictionary', selectedEntry.id));
      if (snap.exists()) {
        setSelectedEntry({ id: snap.id, ...snap.data() });
      }
    } catch (e) {
      console.error('Error refreshing entry:', e);
    }
  }, [selectedEntry]);

  // Build reference display data
  const refSections = [];
  if (selectedEntry) {
    if (selectedEntry.synonymIds && selectedEntry.synonymIds.length > 0) {
      refSections.push({ label: 'Synonyms', ids: selectedEntry.synonymIds, type: 'synonymIds' });
    }
    if (selectedEntry.antonymIds && selectedEntry.antonymIds.length > 0) {
      refSections.push({ label: 'Antonyms', ids: selectedEntry.antonymIds, type: 'antonymIds' });
    }
    if (selectedEntry.registerVariantIds && selectedEntry.registerVariantIds.length > 0) {
      refSections.push({ label: 'Register variants', ids: selectedEntry.registerVariantIds, type: 'registerVariantIds' });
    }
  }

  return (
    <div className="page-shell">
      <div className="page-shell-inner">
        <p className="page-shell-subtitle">Look up and manage French vocabulary</p>

        <DictionarySearch
          onSelect={handleSelect}
          onNotFound={handleNotFound}
          onClear={() => {
            setSelectedEntry(null);
            setShowAddForm(false);
            resetEditing();
          }}
        />

        {/* Show entry when found */}
        {selectedEntry && (
          <div className="dict-page-result">
            {/* Entry header — canonical form + meaning */}
            <div className="dict-page-result-header">
              <h3 className="dict-page-result-canonical">{selectedEntry.canonicalForm}</h3>
              <span className="dict-page-result-meaning">
                {getMeaning(selectedEntry)}
              </span>
              {formatCount(selectedEntry.annotationCount) && (
                <span className="dict-page-result-count">{formatCount(selectedEntry.annotationCount)}</span>
              )}
            </div>

            {/* Quick Add Chips — primary annotation mechanism */}
            <QuickAddChips
              entryId={selectedEntry.id}
              entryCanonicalForm={selectedEntry.canonicalForm}
              onItemAdded={handleDisplayItemSaved}
            />

            {/* Display Items — arrow cards */}
            <DisplayItemsList
              entryId={selectedEntry.id}
              items={selectedEntry.displayItems || []}
              onUpdated={handleDisplayItemsUpdated}
            />

            {/* + Add annotation button / editor */}
            {!showDisplayItemForm ? (
              <button
                className="dict-page-add-annotation-btn"
                onClick={() => setShowDisplayItemForm(true)}
              >
                + Add annotation
              </button>
            ) : (
              <DisplayItemsEditor
                entryId={selectedEntry.id}
                entryCanonicalForm={selectedEntry.canonicalForm}
                onSaved={handleDisplayItemSaved}
                onCancel={() => setShowDisplayItemForm(false)}
              />
            )}

            {/* Reference sections */}
            {refSections.length > 0 && (
              <div className="dict-page-refs">
                {refSections.map((section) => (
                  <RefDisplay
                    key={section.type}
                    label={section.label}
                    entryIds={section.ids}
                    entryId={selectedEntry.id}
                    linkType={section.type}
                    onRemoved={refreshEntry}
                  />
                ))}
              </div>
            )}

            {/* Delete entry */}
            <div className="dict-page-delete-section">
              {!showDeleteConfirm ? (
                <button
                  className="dict-page-delete-btn"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete entry
                </button>
              ) : (
                <div className="dict-page-delete-confirm">
                  <p>Delete "{selectedEntry.canonicalForm}"? This will remove it from the dictionary and unlink it from all related entries.</p>
                  <div className="dict-page-delete-actions">
                    <button
                      className="dict-page-delete-confirm-btn"
                      onClick={handleDelete}
                      disabled={deleting}
                    >
                      {deleting ? 'Deleting…' : 'Confirm Delete'}
                    </button>
                    <button
                      className="dict-page-delete-cancel-btn"
                      onClick={() => setShowDeleteConfirm(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Show add form when not found + confirmed */}
        {showAddForm && (
          <DictionaryEntryForm
            initialCanonicalForm={addFormTerm}
            onSaved={handleSaved}
            onCancel={handleCancelAdd}
          />
        )}
      </div>
    </div>
  );
}

/**
 * RefDisplay — shows a list of linked entry IDs with remove buttons.
 */
function RefDisplay({ label, entryIds, entryId, linkType, onRemoved }) {
  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Lazy-load entry names on first render
  React.useEffect(() => {
    if (loaded || entryIds.length === 0) return;
    loadEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryIds]);

  const loadEntries = async () => {
    const results = [];
    for (const id of entryIds) {
      try {
        const snap = await getDoc(doc(db, 'dictionary', id));
        if (snap.exists()) {
          results.push({ id: snap.id, canonicalForm: snap.data().canonicalForm });
        }
      } catch (e) {
        // skip
      }
    }
    setEntries(results);
    setLoaded(true);
  };

  const handleRemove = async (targetId) => {
    try {
      const currentRef = doc(db, 'dictionary', entryId);
      const targetRef = doc(db, 'dictionary', targetId);

      // Remove bidirectionally
      await updateDoc(currentRef, {
        [linkType]: arrayRemove(targetId),
        lastUpdated: serverTimestamp(),
      });
      await updateDoc(targetRef, {
        [linkType]: arrayRemove(entryId),
        lastUpdated: serverTimestamp(),
      });

      setEntries((prev) => prev.filter((e) => e.id !== targetId));
      onRemoved && onRemoved();
    } catch (e) {
      console.error('Error removing link:', e);
    }
  };

  if (entries.length === 0 && loaded) return null;

  return (
    <div className="dict-page-ref-section">
      <span className="dict-page-ref-label">{label}:</span>
      <div className="dict-page-ref-items">
        {entries.map((e) => (
          <span key={e.id} className="dict-page-ref-item">
            {e.canonicalForm}
            <button
              className="dict-page-ref-remove"
              onClick={() => handleRemove(e.id)}
              aria-label={`Remove ${e.canonicalForm}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default DictionaryPage;
